import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Link,
  CardFooter,
  Spinner,
  Switch,
  Alert,
} from '@heroui/react';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  useGetPatientQuery,
  useUpdatePatientMutation,
} from '@/patient-app-common/api/patientApi';

const mailto =
  'mailto:support@seamless.md?subject=SeamlessMD%20Support%20request%20-%20Account%20Help&body=Please%20include%20the%20following%20information%3A%0A%0APatient%20Name%3A%0AHospital%20Name%3A%0AEmail%3A%0APhone%20number%3A';

export function ConfirmProfile() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const { data: patient, isLoading } = useGetPatientQuery();
  const [updatePatient] = useUpdatePatientMutation();

  const [emailNotification, setEmailNotification] = useState(false);
  const [phoneNotification, setPhoneNotification] = useState(false);
  const patientInitializedRef = useRef(false);

  useEffect(() => {
    if (!patient || patientInitializedRef.current) return;

    setEmailNotification(patient.emailNotification);
    setPhoneNotification(patient.phoneNotification);
    patientInitializedRef.current = true;
  }, [patient]);

  useEffect(() => {
    if (!patient) return;

    if (_.isEmpty(patient.email) && _.isEmpty(patient.phone)) {
      localStorage.setItem('confirmedProfile', 'true');
      handleNext();
      return;
    }
  }, [patient]);

  const debouncedUpdate = useCallback(
    _.debounce(async (emailValue: boolean, phoneValue: boolean) => {
      try {
        await updatePatient({
          emailNotification: emailValue,
          phoneNotification: phoneValue,
        });
      } catch (e) {
        console.log(e);
      }
    }, 800),
    []
  );

  const handleSystemNotifications = (
    value: boolean,
    type: 'email' | 'phone'
  ) => {
    if (type === 'email') {
      setEmailNotification(value);
    } else {
      setPhoneNotification(value);
    }

    debouncedUpdate(
      type === 'email' ? value : emailNotification,
      type === 'phone' ? value : phoneNotification
    );
  };

  const handleNext = useCallback(() => {
    if (!patient) return;

    const { setupSurveyId, setupSurveyCompleted } = patient;

    if (!setupSurveyId || setupSurveyCompleted) {
      return navigate('/home');
    } else {
      return navigate(`/tracker/${setupSurveyId}`);
    }
  }, [patient]);

  const handleExit = () => {
    localStorage.setItem('confirmedProfile', 'true');
    handleNext();
  };

  return (
    <div className="flex flex-col items-center justify-center h-[100dvh]">
      {isLoading && (
        <div className="items-center flex flex-col justify-center">
          <Spinner className="m-4" color="primary" size="lg" />
          <h1 className="text-lg">{t('common.text.loading')}</h1>
        </div>
      )}
      {!_.isEmpty(patient) && (
        <div className="sm:max-w-xl w-full">
          <Card shadow="sm" className="border border-grey px-4">
            <CardHeader>
              <h1 className="text-lg font-semibold">
                {t('confirmprofile.title')}
              </h1>
            </CardHeader>
            <CardBody className="gap-4">
              <p>{t('confirmprofile.confirm_text')}</p>
              <p>
                <strong>{t('common.text.email')}: </strong>
                {patient.email ? patient.email : t('common.text.not_provided')}
              </p>
              <p>
                <strong>{t('common.text.phone')}: </strong>
                {patient.phone ? patient.phone : t('common.text.not_provided')}
              </p>
              <p>{t('confirmprofile.help_text')}</p>
              <Link href={mailto}>support@seamless.md</Link>
              {(patient.email || patient.phone) && (
                <>
                  <h2 className="text-lg font-semibold">
                    {t('confirmprofile.notification_title')}
                  </h2>
                  <p>{t('confirmprofile.notification_text')}</p>
                </>
              )}
              <div className="space-y-4 flex flex-col">
                {!_.isEmpty(patient.email) && (
                  <Switch
                    size="sm"
                    isSelected={emailNotification}
                    color="primary"
                    className="gap-8"
                    onValueChange={(value) =>
                      handleSystemNotifications(value, 'email')
                    }
                  >
                    {t('common.toggle.email_notification')}
                  </Switch>
                )}
                {!_.isEmpty(patient.phone) && (
                  <Switch
                    size="sm"
                    isSelected={phoneNotification}
                    color="primary"
                    className="gap-8"
                    onValueChange={(value) =>
                      handleSystemNotifications(value, 'phone')
                    }
                  >
                    {t('common.toggle.sms_notification')}
                  </Switch>
                )}
              </div>
              <Alert
                color="danger"
                hideIconWrapper
                isVisible={!phoneNotification && !emailNotification}
                title={t('confirmprofile.warning_text')}
              />
            </CardBody>
            <CardFooter>
              <Button
                className="mx-auto w-1/2"
                color="primary"
                onPress={handleExit}
              >
                {t('common.text.continue')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
